import * as styles from "./ReplacerCombobox.css";

import { useMemo, useState } from "react";

import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxVirtualizedList,
  useComboboxVirtualizedScroll,
} from "@frontend/components/atoms/Combobox";
import { EmployeeAvatar } from "@frontend/shared/employee-field";
import type { EmployeeOption } from "@frontend/shared/employee-field";
import { Trans, useLingui } from "@lingui/react/macro";

import { useAvailableEmployees } from "../hooks/useAvailableEmployees";
import type { EmployeeAvailability, ReplacerEmployee } from "../types";

const ROW_HEIGHT = 32;

const DESCRIBED_ROW_HEIGHT = 56;

type ReplacerOption = ReplacerEmployee & {
  taken: boolean;
  absenceLabel: string | null;
};

export type ReplacerComboboxProps = {
  value: ReplacerEmployee | null;
  from: string | null;
  to: string | null;
  disabled?: boolean;
  autoFocus?: boolean;
  onValueChange: (employee: ReplacerEmployee | null) => void;
};

function toOption(employee: EmployeeAvailability): ReplacerOption {
  return {
    id: employee.id,
    label: employee.displayNameWithId,
    avatarUrl: employee.avatarUrl,
    hasPhoto: employee.hasPhoto,
    taken: !employee.available,
    absenceLabel: employee.absenceLabel,
  };
}

function toAvatar(employee: ReplacerEmployee): EmployeeOption {
  return {
    id: employee.id,
    label: employee.label,
    department: null,
    available: true,
    avatarUrl: employee.avatarUrl,
    hasPhoto: employee.hasPhoto,
  };
}

export function ReplacerCombobox({
  value,
  from,
  to,
  disabled,
  autoFocus,
  onValueChange,
}: ReplacerComboboxProps) {
  const { t } = useLingui();

  const [search, setSearch] = useState("");

  const { employees, loading } = useAvailableEmployees(from, to, search);
  const { virtualizerRef, onItemHighlighted } =
    useComboboxVirtualizedScroll<ReplacerOption>();

  const listed = useMemo(() => employees.map(toOption), [employees]);

  const selected = useMemo<ReplacerOption | null>(
    () => (value ? { ...value, taken: false, absenceLabel: null } : null),
    [value],
  );

  const options = useMemo<ReplacerOption[]>(
    () =>
      selected && !listed.some((option) => option.id === selected.id)
        ? [selected, ...listed]
        : listed,
    [listed, selected],
  );

  return (
    <Combobox<ReplacerOption>
      virtualized
      disabled={disabled}
      items={options}
      value={selected}
      filter={null}
      itemToStringLabel={(option) => option?.label ?? ""}
      isItemEqualToValue={(a, b) => a.id === b.id}
      onItemHighlighted={onItemHighlighted}
      onInputValueChange={(text, { reason }) => {
        if ("input-change" === reason || "input-clear" === reason) {
          setSearch(text);
        }
      }}
      onOpenChange={(open) => {
        if (!open) {
          setSearch("");
        }
      }}
      onValueChange={(option) =>
        onValueChange(
          option
            ? {
                id: option.id,
                label: option.label,
                avatarUrl: option.avatarUrl,
                hasPhoto: option.hasPhoto,
              }
            : null,
        )
      }
    >
      <ComboboxInput
        placeholder={t`Search an employee`}
        autoFocus={autoFocus}
        loading={loading}
        onFocus={(event) => event.currentTarget.select()}
        leftSection={
          selected ? (
            <EmployeeAvatar employee={toAvatar(selected)} size="auto" />
          ) : null
        }
      />

      <ComboboxContent
        popupWidth="anchor"
        empty={<Trans>Nobody matches this search.</Trans>}
      >
        <ComboboxVirtualizedList<ReplacerOption>
          estimateSize={(index) =>
            options[index]?.absenceLabel ? DESCRIBED_ROW_HEIGHT : ROW_HEIGHT
          }
          itemKey={(option) => String(option.id)}
          description={(option) => option.absenceLabel}
          virtualizerRef={virtualizerRef}
        >
          {(option) => (
            <span className={styles.item}>
              <EmployeeAvatar
                employee={toAvatar(option)}
                size="auto"
                className={styles.itemAvatar}
              />

              <span
                className={
                  option.taken
                    ? `${styles.itemLabel} ${styles.taken}`
                    : styles.itemLabel
                }
              >
                {option.label}
              </span>
            </span>
          )}
        </ComboboxVirtualizedList>
      </ComboboxContent>
    </Combobox>
  );
}
